/**
 * Cross-device sync client — pure logic, tested.
 *
 * Talks to the Worker API in worker/index.js. Deliberately kept free of React
 * so the conflict rules can be tested without rendering anything.
 *
 * Design notes that matter:
 *
 * localStorage stays the source of truth while you are using the app. Sync is a
 * copy operation at explicit moments, not a live mirror — the app must keep
 * working identically with no account, offline, or when the Worker is down.
 *
 * The server detects conflicts rather than resolving them. PUT carries the
 * `updatedAt` the client last saw; if the server has moved on it answers 409
 * with its own copy instead of overwriting. Nothing is discarded without the
 * user choosing, which matters when one person logs on a phone at the reservoir
 * while another edits on a laptop.
 */

import { storage } from "./storage.js";

/** The six keys that make up a user's farm data. Order is stable for tests. */
export const SYNC_KEYS = [
  "active-grow-id",
  "farm-grows",
  "farm-logs",
  "power-settings",
  "system-settings",
  "user-location",
];

/**
 * Account state is shown in two places — the header and the Settings panel — and
 * they must never disagree. Rather than lifting state through App.jsx, which is
 * one very large component, both subscribe here and this module notifies on any
 * change that affects who is signed in or what was last synced.
 */
const bus = new EventTarget();

export function onAccountChange(handler) {
  const fn = () => handler();
  bus.addEventListener("change", fn);
  return () => bus.removeEventListener("change", fn);
}

function announce() {
  bus.dispatchEvent(new Event("change"));
}

const TOKEN_KEY = "sync-token";
const META_KEY = "sync-meta"; // { updatedAt, email, name, provider }

/** Stable per-browser id so the server can record which device wrote last. */
export function deviceId() {
  try {
    let id = window.localStorage.getItem("yaadgrow:device-id");
    if (!id) {
      id = (crypto?.randomUUID?.() || String(Math.random()).slice(2)) + "";
      window.localStorage.setItem("yaadgrow:device-id", id);
    }
    return id;
  } catch {
    return "unknown";
  }
}

export async function getToken() {
  const row = await storage.get(TOKEN_KEY);
  return row ? row.value : null;
}

export async function getMeta() {
  const row = await storage.get(META_KEY);
  if (!row) return null;
  try {
    return JSON.parse(row.value);
  } catch {
    return null;
  }
}

async function setMeta(meta) {
  await storage.set(META_KEY, JSON.stringify(meta));
}

export async function isSignedIn() {
  return (await getToken()) !== null;
}

/** Read the six keys out of local storage into one plain object. */
export async function collectLocal() {
  const out = {};
  for (const key of SYNC_KEYS) {
    const row = await storage.get(key);
    if (row) out[key] = row.value;
  }
  return out;
}

/**
 * Write a pulled payload back into local storage.
 *
 * Keys absent from the payload are deleted rather than left alone: the payload
 * is a complete snapshot, so keeping a stale local key would silently resurrect
 * a grow the user deleted on their other device.
 */
export async function applyRemote(data) {
  for (const key of SYNC_KEYS) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      await storage.set(key, data[key]);
    } else {
      await storage.delete(key);
    }
  }
}

async function api(path, { method = "GET", body, token } = {}) {
  const res = await fetch(path, {
    method,
    headers: {
      ...(body ? { "content-type": "application/json" } : {}),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  let payload = null;
  try {
    payload = await res.json();
  } catch {
    // Non-JSON error page (a proxy, an outage). Leave payload null.
  }
  return { status: res.status, ok: res.ok, payload };
}

/** Exchange a Google ID token for a session. Returns { email, name }. */
export async function signIn(credential) {
  const { ok, status, payload } = await api("/api/auth", {
    method: "POST",
    body: { credential },
  });
  if (!ok) {
    throw new Error(
      status === 500
        ? "Sync is not configured on the server yet."
        : (payload && payload.error) || "Sign-in failed."
    );
  }
  await storage.set(TOKEN_KEY, payload.token);
  const meta = { updatedAt: 0, ...(payload.user || {}) };
  await setMeta(meta);
  announce();
  return meta;
}

/** Forget the session. Farm data on this device is left untouched. */
export async function signOut() {
  await storage.delete(TOKEN_KEY);
  await storage.delete(META_KEY);
  announce();
}

/**
 * Pull the server copy over local data.
 *
 * Returns { applied: false } when the account has never synced, so the caller
 * can leave local data alone rather than wiping it with an empty payload.
 */
export async function pull() {
  const token = await getToken();
  if (!token) throw new Error("Not signed in.");
  const { ok, status, payload } = await api("/api/data", { token });
  if (status === 401) {
    await signOut();
    throw new Error("Session expired — sign in again.");
  }
  if (!ok) throw new Error((payload && payload.error) || "Could not load.");
  if (!payload.data) return { applied: false, updatedAt: 0 };

  await applyRemote(payload.data);
  const meta = (await getMeta()) || {};
  await setMeta({ ...meta, updatedAt: payload.updatedAt });
  announce();
  return { applied: true, updatedAt: payload.updatedAt };
}

/**
 * Push local data to the server.
 *
 * On 409 the server's copy is returned rather than applied — the caller decides.
 * Returning it here avoids a second round trip to fetch what the server already
 * handed back with the conflict.
 */
export async function push() {
  const token = await getToken();
  if (!token) throw new Error("Not signed in.");
  const meta = (await getMeta()) || { updatedAt: 0 };
  const data = await collectLocal();

  const { ok, status, payload } = await api("/api/data", {
    method: "PUT",
    token,
    body: { data, baseUpdatedAt: meta.updatedAt || 0, device: deviceId() },
  });

  if (status === 401) {
    await signOut();
    throw new Error("Session expired — sign in again.");
  }
  if (status === 409) {
    return { conflict: true, serverData: payload.data, serverUpdatedAt: payload.updatedAt };
  }
  if (!ok) throw new Error((payload && payload.error) || "Could not save.");

  await setMeta({ ...meta, updatedAt: payload.updatedAt });
  return { conflict: false, updatedAt: payload.updatedAt };
}

/**
 * Resolve a conflict by taking the server's copy.
 *
 * Separate from pull() because the payload is already in hand — re-fetching
 * would race against whatever wrote in between.
 */
export async function acceptServer(serverData, serverUpdatedAt) {
  await applyRemote(serverData);
  const meta = (await getMeta()) || {};
  await setMeta({ ...meta, updatedAt: serverUpdatedAt });
}

/**
 * Resolve a conflict by overwriting the server with local data.
 *
 * Adopts the server's `updatedAt` as the base first, so the retried PUT is not
 * rejected as another conflict.
 */
export async function overwriteServer(serverUpdatedAt) {
  const meta = (await getMeta()) || {};
  await setMeta({ ...meta, updatedAt: serverUpdatedAt });
  return push();
}
